import React, { useState } from 'react';
import {
    AppBar,
    Toolbar,
    Typography,
    IconButton,
    Tooltip,
    Avatar,
    useMediaQuery,
    useTheme,
    Box,
    Chip,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Divider,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import EditIcon from '@mui/icons-material/Edit';
import PersonIcon from '@mui/icons-material/Person';
import NotificationMenu from './NotificationMenu';
import { useAuth } from '../../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const UserHeaderBar = ({ onToggleSidebar, onLogout }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handleAvatarClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleEditProfile = () => {
        handleClose();
        navigate('/dashboard/profile');
    };

    const handleLogout = () => {
        handleClose();
        onLogout();
    };

    // Pendant le chargement de l'auth
    if (loading) {
        return (
            <AppBar
                position="fixed"
                elevation={0}
                sx={{
                    width: { sm: `calc(100% - 260px)` },
                    ml: { sm: `260px` },
                    bgcolor: 'background.paper',
                    borderBottom: '1px solid rgba(0,0,0,0.1)',
                    zIndex: 1,
                }}
            >
                <Toolbar>
                    {isMobile && (
                        <IconButton onClick={onToggleSidebar} sx={{ mr: 2 }}>
                            <MenuIcon />
                        </IconButton>
                    )}

                    <Typography variant="h6" sx={{ flexGrow: 1 }}>
                        Tej Flow
                    </Typography>

                    <Box sx={{ mr: 2 }}>
                        <Chip
                            icon={<AccountBalanceWalletIcon />}
                            label="…"
                            variant="outlined"
                            sx={{ fontWeight: 600 }}
                        />
                    </Box>
                </Toolbar>
            </AppBar>
        );
    }

    const rawPoints = user?.points;
    const points = rawPoints == null ? null : Number(rawPoints);
    const pointsLabel = points == null || Number.isNaN(points)
        ? '—'
        : Intl.NumberFormat('fr-FR').format(points);

    const fullName = `${user?.prenom ?? ''} ${user?.nom ?? ''}`.trim();
    const avatarInitial = (user?.prenom?.[0] || user?.nom?.[0] || 'U').toUpperCase();

    return (
        <AppBar
            position="fixed"
            elevation={0}
            sx={{
                width: { sm: `calc(100% - 260px)` },
                ml: { sm: `260px` },
                bgcolor: 'background.paper',
                borderBottom: '1px solid rgba(0,0,0,0.1)',
                zIndex: 1,
            }}
        >
            <Toolbar>
                {isMobile && (
                    <IconButton onClick={onToggleSidebar} sx={{ mr: 2 }}>
                        <MenuIcon />
                    </IconButton>
                )}

                <Typography variant="h6" sx={{ flexGrow: 1 }}>
                    Tej Flow
                </Typography>

                {/* Solde de points */}
                <Box sx={{ mr: 2 }}>
                    <Tooltip title="Solde de points">
                        <Chip
                            icon={<AccountBalanceWalletIcon />}
                            label={pointsLabel}
                            variant="outlined"
                            color={points === 0 ? 'warning' : 'default'}
                            sx={{ fontWeight: 600 }}
                        />
                    </Tooltip>
                </Box>

                <Box sx={{ mr: 2 }}>
                    <NotificationMenu />
                </Box>

                {/* Avatar avec menu dropdown */}
                <Tooltip title={fullName || "Profil"}>
                    <IconButton
                        onClick={handleAvatarClick}
                        size="small"
                        sx={{ ml: 2 }}
                        aria-controls={open ? 'account-menu' : undefined}
                        aria-haspopup="true"
                        aria-expanded={open ? 'true' : undefined}
                    >
                        <Avatar sx={{ bgcolor: theme.palette.secondary.main, width: 40, height: 40 }}>
                            {avatarInitial}
                        </Avatar>
                    </IconButton>
                </Tooltip>

                <Menu
                    anchorEl={anchorEl}
                    id="account-menu"
                    open={open}
                    onClose={handleClose}
                    onClick={handleClose}
                    PaperProps={{
                        elevation: 3,
                        sx: {
                            overflow: 'visible',
                            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                            mt: 1.5,
                            '& .MuiAvatar-root': {
                                width: 32,
                                height: 32,
                                ml: -0.5,
                                mr: 1,
                            },
                        },
                    }}
                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                >
                    <MenuItem disabled>
                        <ListItemIcon>
                            <PersonIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText 
                            primary={fullName}
                            secondary={user?.email}
                            primaryTypographyProps={{ 
                                fontWeight: 600,
                                fontSize: '0.9rem'
                            }}
                            secondaryTypographyProps={{ 
                                fontSize: '0.75rem',
                                color: 'text.secondary'
                            }}
                        />
                    </MenuItem>
                    <Divider />
                    <MenuItem onClick={handleEditProfile}>
                        <ListItemIcon>
                            <EditIcon fontSize="small" />
                        </ListItemIcon>
                        Modifier le profil
                    </MenuItem>
                    <MenuItem onClick={handleLogout}>
                        <ListItemIcon>
                            <LogoutIcon fontSize="small" />
                        </ListItemIcon>
                        Déconnexion
                    </MenuItem>
                </Menu>
            </Toolbar>
        </AppBar>
    );
};

export default UserHeaderBar;